import React, { FC } from 'react';

import {
    EXTERNAL_PRODUCTS,
    PRODUCTS,
    THREEBTYPES,
} from 'constants/constants';
import { Loader, Title } from 'ui-library';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';
import ProductSummaryItem from '../ProductSummaryItem/ProductSummaryItem';
import arrayContains from '../../../../utils/arrayUtils';

type ProductSummaryProps = {
    t: (key: string) => any;
    loadingAppsByContainer: boolean;
    sortedListOfApplications: any[];
    getPeriodicityLabel: (periodicity: any) => any;
    getWithdrawalFrequencyLabel: (frequency: any) => any;
    getApplicationType: (app: any) => any;
};

const ProductSummary: FC<ProductSummaryProps> = ({
    t,
    loadingAppsByContainer,
    sortedListOfApplications,
    getPeriodicityLabel,
    getWithdrawalFrequencyLabel,
    getApplicationType,
}) => (
    <>
        <Title
            className="product-summary-title font-normal"
            type={2}
        >
            {t(
                'portfolio.createNewPortfolio.opening.productSummary',
            )}
        </Title>
        {loadingAppsByContainer ? (
            <div className="product-summary-data">
                <Loader />
            </div>
        ) : (
            <div className="product-summary-data">
                {sortedListOfApplications?.map((app) => {
                    const combinedApp = app?.applicationData ?? app;
                    const isPK = combinedApp.investmentDescription
                        ?.productExternalId === THREEBTYPES.pk;
                    const isZic = combinedApp.investmentDescription
                        ?.productExternalId === THREEBTYPES.zic;
                    const is3a = combinedApp.investmentDescription
                        ?.productExternalId === PRODUCTS.aaa;
                    const is3ap = arrayContains(
                        [EXTERNAL_PRODUCTS.aaap],
                        combinedApp.investmentDescription
                            ?.productExternalId,
                    );
                    const isFz = arrayContains(
                        [
                            EXTERNAL_PRODUCTS.fz,
                            EXTERNAL_PRODUCTS.fzp,
                        ],
                        combinedApp.investmentDescription
                            ?.productExternalId,
                    );
                    const isAZP = arrayContains(
                        [
                            THREEBTYPES.azpNeuSB,
                            THREEBTYPES.azpAlt,
                            THREEBTYPES.azpNeu,
                        ],
                        combinedApp.investmentDescription
                            ?.productExternalId,
                    );

                    const fees = isZic
                        ? combinedApp.investmentDescription?.fees?.filter(
                            (f) => f.feeType === FEE_TYPES.entry,
                        )
                        : [];
                    const entryCostFee = fees?.length > 0 ? fees[0].fee : 0;

                    return (
                        <ProductSummaryItem
                            key={
                                combinedApp.investmentApplicationId
                            }
                            oneTimeInvestment={
                                combinedApp?.changePlan
                                    ? combinedApp?.plannedInvestmentAmount
                                    : combinedApp
                                        ?.investmentDescription
                                        ?.productConfiguration
                                        ?.initialInvestment
                            }
                            periodicPayment={
                                combinedApp?.investmentDescription
                                    ?.productConfiguration
                                    ?.periodicalPaymentAmount
                            }
                            frequency={t(
                                getPeriodicityLabel(
                                    combinedApp
                                        ?.investmentDescription
                                        ?.productConfiguration
                                        ?.periodicity,
                                ),
                            )}
                            category={
                                combinedApp.investmentDescription
                                    .productConfiguration?.rubrik
                            }
                            transfer={
                                combinedApp.investmentDescription
                                    .productConfiguration
                                    ?.yearlyPayments
                            }
                            pensionSchema={
                                combinedApp.investmentDescription
                                    .productConfiguration
                                    ?.pensionScheme
                            }
                            isFullTransfer={
                                combinedApp.investmentDescription
                                    .productConfiguration
                                    ?.isFullTransfer
                            }
                            strategy={
                                combinedApp?.strategy
                            || app.strategy
                            }
                            isPk={isPK}
                            isZic={isZic}
                            is3a={is3a}
                            is3ap={is3ap as boolean}
                            isFz={isFz as boolean}
                            isAZP={isAZP as boolean}
                            withdrawal={
                                combinedApp.investmentDescription
                                    ?.withdrawal
                                    ? {
                                        amount: combinedApp
                                            .investmentDescription
                                            .withdrawal
                                            ?.withdrawalAmount,
                                        frequency:
                                          getWithdrawalFrequencyLabel(
                                              combinedApp
                                                  .investmentDescription
                                                  .withdrawal
                                                  ?.periodicity,
                                          ),
                                        start: combinedApp
                                            .investmentDescription
                                            .withdrawal
                                            ?.startDate,
                                        plannedEndDate:
                                          combinedApp
                                              .investmentDescription
                                              .withdrawal
                                              ?.plannedEndDate,
                                        iban: combinedApp
                                            .investmentDescription
                                            .paymentInstruction
                                            ?.iban,
                                        holder: `${combinedApp.investmentDescription.paymentInstruction?.firstName} ${combinedApp.investmentDescription.paymentInstruction?.lastName}`,
                                        bank: `${combinedApp.investmentDescription.paymentInstruction?.bankName}, ${combinedApp.investmentDescription.paymentInstruction?.zipCity} ${combinedApp.investmentDescription.paymentInstruction?.city}`,
                                    }
                                    : undefined
                            }
                            applicationType={getApplicationType(
                                combinedApp,
                            )}
                            depotNr={
                                combinedApp?.portfolioInfo?.name
                            }
                            productName={`${
                                isPK
                                    ? ''
                                    : `${combinedApp?.productName} `
                            }${
                                combinedApp?.strategy
                            || app.strategy
                            }`}
                            oldStrategy={
                                combinedApp?.portfolioInfo?.strategy
                            }
                            isCEP={combinedApp?.changePlan}
                            issueCommission={
                                combinedApp?.investmentDescription
                                    ?.agentInformation
                                    ?.issueCommission
                            }
                            entryCostFee={entryCostFee}
                            pbNumber={
                                combinedApp?.investmentDescription
                                    ?.agentInformation?.pbNumber
                            || ''
                            }
                            currentInvestment={
                                combinedApp?.portfolioInfo
                                    ?.currentValue
                            }
                            amountToBeBought={
                                app?.investmentDescription
                                    ?.changeInvestmentAmount?.isSell
                                    ? null
                                    : app?.investmentDescription
                                        ?.changeInvestmentAmount
                                        ?.value
                            }
                            amountToBeSold={
                                app?.investmentDescription
                                    ?.changeInvestmentAmount?.isSell
                                    ? app?.investmentDescription
                                        ?.changeInvestmentAmount
                                        ?.value
                                    : null
                            }
                        />
                    );
                })}
            </div>
        )}
    </>
);

export default ProductSummary;
